import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
  Divider,
} from '@mui/material'
import { ArrowBack, Visibility, VisibilityOff, CheckCircle, Error, Business, Work } from '@mui/icons-material'
import Button from '@components/common/Button'
import api from '@services/api'
import { toast } from 'react-toastify'

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [tokenError, setTokenError] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [success, setSuccess] = useState(false)
  const [inviteInfo, setInviteInfo] = useState({
    email: '',
    first_name: '',
    last_name: '',
    department: '',
    job_title: '',
  })

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
      password_confirm: '',
    },
  })

  const password = watch('password')

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('No invitation token provided.')
        setValidating(false)
        return
      }

      try {
        const response = await api.get(`/auth/accept-invitation/?token=${token}`)
        if (response.data.valid) {
          setTokenValid(true)
          setInviteInfo({
            email: response.data.email,
            first_name: response.data.first_name,
            last_name: response.data.last_name,
            department: response.data.department,
            job_title: response.data.job_title,
          })
        } else {
          setTokenError(response.data.error || 'Invalid or expired invitation.')
        }
      } catch (err) {
        setTokenError('Failed to validate invitation.')
      } finally {
        setValidating(false)
      }
    }

    validateToken()
  }, [token])

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      await api.post('/auth/accept-invitation/', {
        token,
        username: data.username,
        password: data.password,
        password_confirm: data.password_confirm,
      })
      setSuccess(true)
      toast.success('Account created successfully!')
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.username?.[0] || 'Failed to create account.')
    } finally {
      setLoading(false)
    }
  }

  if (validating) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F5F7FA',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F7FA',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: '100%',
          p: 4,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Logo */}
        <Box textAlign="center" mb={4}>
          <Box
            sx={{
              width: 60,
              height: 60,
              backgroundColor: '#1B5E7A',
              borderRadius: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" fontWeight={700} color="white">
              UCI
            </Typography>
          </Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Accept Invitation
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Create your DuoNep account
          </Typography>
        </Box>

        {/* Token Invalid */}
        {!tokenValid && tokenError && (
          <Box textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#FFEBEE',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Error sx={{ fontSize: 32, color: '#F44336' }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Invalid Invitation
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {tokenError}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Please contact your administrator for a new invitation.
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="outlined"
              startIcon={<ArrowBack />}
              fullWidth
            >
              Go to Login
            </Button>
          </Box>
        )}

        {/* Success */}
        {success && (
          <Box textAlign="center">
            <Box
              sx={{
                width: 64,
                height: 64,
                backgroundColor: '#E8F5E9',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <CheckCircle sx={{ fontSize: 32, color: '#4CAF50' }} />
            </Box>
            <Typography variant="h6" gutterBottom>
              Account Created Successfully
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Welcome to DuoNep! You can now log in with your credentials.
            </Typography>
            <Button
              component={Link}
              to="/login"
              fullWidth
            >
              Go to Login
            </Button>
          </Box>
        )}

        {/* Accept Form */}
        {tokenValid && !success && (
          <>
            {/* Invitation Details */}
            <Box
              sx={{
                backgroundColor: '#f8fafc',
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Your account details
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {inviteInfo.first_name} {inviteInfo.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {inviteInfo.email}
              </Typography>
              {(inviteInfo.department || inviteInfo.job_title) && (
                <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                  {inviteInfo.department && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Business fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {inviteInfo.department}
                      </Typography>
                    </Box>
                  )}
                  {inviteInfo.job_title && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Work fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {inviteInfo.job_title}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="subtitle2" gutterBottom>
              Choose your login credentials
            </Typography>

            <form onSubmit={handleSubmit(onSubmit)}>
              <TextField
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Username must be at least 3 characters',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Username can only contain letters, numbers, and underscores',
                  },
                })}
                fullWidth
                label="Username"
                error={!!errors.username}
                helperText={errors.username?.message || 'Choose a unique username for logging in'}
                margin="normal"
                autoComplete="username"
                autoFocus
              />

              <TextField
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                })}
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                error={!!errors.password}
                helperText={errors.password?.message}
                margin="normal"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...register('password_confirm', {
                  required: 'Please confirm your password',
                  validate: (value) =>
                    value === password || 'Passwords do not match',
                })}
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                error={!!errors.password_confirm}
                helperText={errors.password_confirm?.message}
                margin="normal"
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                Create Account
              </Button>
            </form>

            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Link to="/login" style={{ color: '#1B5E7A', textDecoration: 'none' }}>
                  Sign in
                </Link>
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  )
}

export default AcceptInvitation
